import { Messages } from '../../codegen/Localize';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { GetServerSettingsUnauthenticatedRequest } from '../ServerApi/requests/serverSettings/GetServerSettingsUnauthenticatedRequest';
import { AuthenticationType,DomainFamily, IPreloginCustomization, IServerCustomization, IServerSettingsUnauthenticated } from '../types/IServerSettingsUnauthenticated';

class ServerSettingsUnauthenticatedService {
  private serverSettingsUnauthenticated: IServerSettingsUnauthenticated;

  public fetchServerSettingsUnauthenticated(): Promise<void> {
    return WebHttpClient.sendRequest(new GetServerSettingsUnauthenticatedRequest())
      .then((result: IServerSettingsUnauthenticated) => {
        this.serverSettingsUnauthenticated = result;
      });
  }

  public getAuthenticationType(): AuthenticationType {
    return this.serverSettingsUnauthenticated?.authenticationType?.type;
  }

  public isIdentityPoolsEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.identityPoolsEnabled;
  }

  public isPasswordResetEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.passwordResetEnabled;
  }

  public domainFamily(): string {
    return this.serverSettingsUnauthenticated?.domainFamily;
  }

  public isServerDomainFamilyActiveDirectory(): boolean {
    return this.serverSettingsUnauthenticated?.domainFamily === DomainFamily.ActiveDirectory;
  }

  public isServerDomainFamilyLocal(): boolean {
    return this.serverSettingsUnauthenticated?.domainFamily === DomainFamily.Local;
  }

  public isServerDomainFamilyExternal(): boolean {
    return this.serverSettingsUnauthenticated?.domainFamily === DomainFamily.External;
  }

  public getSamlServiceEntityId(): string {
    return this.serverSettingsUnauthenticated?.samlServiceEntityId;
  }

  public licenseExpirationDateIfWithinWarningPeriod(): string {
    return this.serverSettingsUnauthenticated?.licenseExpirationDate;
  }

  public serverCustomization(): IServerCustomization {
    return this.serverSettingsUnauthenticated?.customization;
  }

  public serverName(): string {
    const customization = this.serverCustomization();
    return customization?.serverName ?? Messages.title();
  }

  public dsdPhoneSize(): number {
    return this.serverSettingsUnauthenticated?.dsdPhoneSize || 0;
  }

  public dsdTabletSize(): number {
    return this.serverSettingsUnauthenticated?.dsdTabletSize || 0;
  }

  public serverLanguage(): string {
    return this.serverSettingsUnauthenticated?.defaultLanguage;
  }

  public serverLocale(): string {
    return this.serverSettingsUnauthenticated?.defaultLocale;
  }

  public isGuestEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.guestEnabled;
  }

  public isWgEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.wgEnabled;
  }

  public preloginCustomization(): IPreloginCustomization {
    return this.serverSettingsUnauthenticated?.preloginCustomization;
  }

  public isSiteSamlEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.siteSamlEnabled;
  }

  public isSignInNoteEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.signInNoteEnabled;
  }

  public getSignInNoteText(): string {
    return this.serverSettingsUnauthenticated?.signInNoteText;
  }

  public getSignInNoteUrl(): string {
    return this.serverSettingsUnauthenticated?.signInNoteUrl;
  }

  public isEnableDeepLinking(): boolean {
    return this.serverSettingsUnauthenticated?.enableDeepLinking;
  }

  public hideAdminVizStatsForLoadTimes(): boolean {
    return this.serverSettingsUnauthenticated?.hideAdminVizStatsForLoadTimes;
  }

  public hideAdminVizPerformanceofViews(): boolean {
    return this.serverSettingsUnauthenticated?.hideAdminVizPerformanceOfViews;
  }

  public isTableauIdSupported(): boolean {
    return this.serverSettingsUnauthenticated?.tableauIdSupported;
  }

  public isConnectedClientsVisible(): boolean {
    return this.serverSettingsUnauthenticated?.connectedClientsVisible;
  }

  public isBridgeEnabled(): boolean {
    return this.serverSettingsUnauthenticated?.bridgeEnabled;
  }

  public isEmbeddedVizForSiteSamlSupported(): boolean {
    return this.serverSettingsUnauthenticated?.embeddedVizForSiteSamlSupported;
  }

  public isDesktopSamlDisabled(): boolean {
    return this.serverSettingsUnauthenticated?.desktopSamlDisabled;
  }
}

export const ServerSettingsUnauthenticated = new ServerSettingsUnauthenticatedService();
export type ServerSettingsUnauthenticated = ServerSettingsUnauthenticatedService;
